import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Button, Input, Label, Card, CardHeader, CardBody } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import fetchFormApi from "./fetchFormApi";
import API from "../../../api/baseApi";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
// import PreviewForm from "./previewForm"; // keep if you actually use it

// File type options for file upload dropdown
const FILE_TYPE_OPTIONS = [
  { label: "All Files", value: "*/*" },
  { label: "PNG Image", value: "image/png" },
  { label: "JPEG Image", value: "image/jpeg" },
  { label: "PDF", value: "application/pdf" },
  { label: "Word Document", value: "application/msword" },
  {
    label: "Word (docx)",
    value:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  { label: "Excel", value: "application/vnd.ms-excel" },
  { label: "ZIP", value: "application/zip" },
  { label: "Text File", value: "text/plain" },
  { label: "CSV", value: "text/csv" },
  { label: "SVG", value: "image/svg+xml" },
];

// Helpers
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// Palette of draggable inputs for the builder
const PALETTE = [
  {
    name: "Text",
    type: "text",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
  },
  {
    name: "Number",
    type: "number",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
  },
  {
    name: "DatePicker",
    type: "date",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
  },
  {
    name: "Time picker",
    type: "time",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
  },
  {
    name: "Preview (HTML)",
    type: "note",
    position: { row: "", column: "" },
    // mark this as a preview-only field (no label/key/placeholder)
    isPreview: true,
    // editable HTML content stored here
    noteHtml: "",
    // conditional rendering (same API as other inputs)
    isCondition: false,
    conditionalRender: {},
  },
  {
    name: "File Upload",
    type: "file",
    label: "",
    key: "Upload_File",
    formKey: "Upload_File",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    allowedFileTypes: [],
    maxFileSize: 0,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
  },
  {
    name: "Dropdown",
    type: "select",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    options: [],
    optionKey: "key",
    optionValue: "value",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    apiLink: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
    isCURL: false,
    cUrlLabel: "",
    cUrlValue: "",
    cURL: "",
  },
  {
    name: "Radio",
    type: "radio",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    options: [],
    optionKey: "key",
    optionValue: "value",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempOptions: [],
    tempRadioOptions: { label: "", key: "" },
    radioOptions: [{ label: "", key: "" }],
  },
  {
    name: "Checkbox",
    type: "checkbox",
    label: "",
    key: "",
    formKey: "",
    placeholder: "",
    position: { row: "", column: "" },
    required: true,
    isPattern: false,
    pattern: "",
    isCondition: false,
    conditionalRender: {},
    tempCheckoboxOptions: {
      label: "",
      key: "",
    },
    checkboxOptions: [
      {
        label: "",
        key: "",
      },
    ],
  },
];

const METHOD_OPTIONS = [
  { name: "GET", value: "get" },
  { name: "POST", value: "post" },
  // { name: "PUT", value: "put" },
  // { name: "PATCH", value: "patch" },
  //  { name: "delete", value: "patch" },
];
const PATTERNS = [
  { name: "Email", value: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$" },
  {
    name: "URL",
    value:
      "^(https?:\\/\\/)?([\\w\\-])+(\\.[\\w\\-]+)+([\\w.,@?^=%&:/~+#-]*[\\w@?^=%&/~+#-])?$",
  },
  { name: "Phone Number", value: "^\\+?[1-9]\\d{7,14}$" },
  {
    name: "IPv4 Address",
    value:
      "^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$",
  },
  { name: "Hex Color", value: "^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$" },
  { name: "Date (YYYY-MM-DD)", value: "^\\d{4}-\\d{2}-\\d{2}$" },
  { name: "Time (HH:MM)", value: "^([01]\\d|2[0-3]):([0-5]\\d)$" },
  { name: "ZIP Code (US)", value: "^\\d{5}(?:-\\d{4})?$" },
  { name: "Username", value: "^[a-zA-Z0-9._-]{3,16}$" },
  {
    name: "Password (Strong)",
    value:
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$",
  },
  { name: "Alphabetic String", value: "^[A-Za-z ]+$" },
  { name: "Numeric String", value: "^\\d+$" },
  { name: "Alphanumeric String", value: "^[A-Za-z0-9]+$" },
  { name: "HTML Tags", value: "<\\/?[a-z][\\s\\S]*>" },
  { name: "Whitespace", value: "^\\s*$" },
  {
    name: "Credit Card Number",
    value:
      "^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9]{2})[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})$",
  },
  { name: "Only Numbers", value: "^[0-9]+$" },
  { name: "Only Alphabets", value: "^[a-zA-Z]+$" },
  { name: "First Letter Capital", value: "^[A-Z][a-z]*$" },
  { name: "No Special Characters", value: "^[a-zA-Z0-9\\s]+$" },
  { name: "Lowercase Letters Only", value: "^[a-z]+$" },
  { name: "Uppercase Letters Only", value: "^[A-Z]+$" },
];

const emptyCell = () => ({ input: null });
const emptyRow = () => ({ columns: [emptyCell()] });

const FormBuilder = forwardRef((props, ref) => {
  const { setForm, setJson, setType, formData, jsonData } = props;

  const [grid, setGrid] = useState([]);
  const [formType, setFormType] = useState("form"); // 'form' | 'json'
  const [jsonValidator, setJSONValidator] = useState({ error: "" });
  const [jsonDataStr, setJSONDataStr] = useState("");
  const [conditionOptions, setConditionOptions] = useState([]); // [{key, value: options[]}]
  /***URL Options */
  const [apiOptions, setApiOptions] = useState({});

  // Drag state
  const [dragState, setDragState] = useState({
    active: false,
    item: null, // palette item
    pos: { x: 0, y: 0 },
    start: { x: 0, y: 0 },
  });
  const [hoverCell, setHoverCell] = useState({ row: null, column: null });
  const [minimizedCells, setMinimizedCells] = useState({}); // keyed by "row-col"

  const toggleMinimize = (rowIndex, columnIndex) => {
    const key = `${rowIndex}-${columnIndex}`;
    setMinimizedCells((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const containerRef = useRef(null);

  // --------- Imperative handle (same API) ----------
  useImperativeHandle(ref, () => ({
    async set() {
      setForm(grid);
    },
  }));

  // --------- Sync incoming props ----------
  useEffect(() => {
    if (Array.isArray(formData) && formData.length) {
      // Normalize inputs to our expected shape
      const safe = formData.map((r) => ({
        ...(r.enableAddMore !== undefined ? { enableAddMore: r.enableAddMore } : {}),
        columns: r?.columns?.map((c) => ({
          input: c?.input ?? null,
        })) ?? [emptyCell()],
      }));
      setGrid(safe);
    }
    if (Array.isArray(jsonData) && jsonData.length) {
      setJSONDataStr(JSON.stringify(jsonData, null, 2));
    }
  }, [formData, jsonData]);

  // --------- Recompute condition options whenever grid changes ----------
  useEffect(() => {
    const options = [];
    grid.forEach((row, rIdx) => {
      row.columns.forEach((col, cIdx) => {
        const inp = col?.input;
        if (!inp || !inp.key || !inp.type) return;

        // Radio: options stored in radioOptions [{label, key}]
        if (inp.type === "radio") {
          const normalized = (Array.isArray(inp.radioOptions) ? inp.radioOptions : [])
            .filter((o) => o.label || o.key)
            .map((o) => ({ key: String(o.label || o.key), value: String(o.key) }));
          options.push({ key: inp.key, label: inp.label || inp.key, value: normalized, type: 'radio', rIdx, cIdx });
          return;
        }

        // Checkbox: options stored in checkboxOptions [{label, key}]
        if (inp.type === "checkbox") {
          const normalized = (Array.isArray(inp.checkboxOptions) ? inp.checkboxOptions : [])
            .filter((o) => o.label || o.key)
            .map((o) => ({ key: String(o.label || o.key), value: String(o.key) }));
          options.push({ key: inp.key, label: inp.label || inp.key, value: normalized, type: 'checkbox', rIdx, cIdx });
          return;
        }

        // Select: prefer explicit options, fall back to api/schema previews
        if (inp.type === "select") {
          let rawOptions = Array.isArray(inp.options) ? inp.options : [];
          if ((!rawOptions || rawOptions.length === 0) && (inp.sourceType === 'api' || inp.isAPI)) {
            const k = 'apiOptions' + rIdx + cIdx;
            rawOptions = apiOptions?.[k] || apiOptions?.["apiOptions" + rIdx + cIdx] || rawOptions;
          }
          if ((!rawOptions || rawOptions.length === 0) && (inp.sourceType === 'schema' || inp.isSchema)) {
            const k = 'schemaOptions' + rIdx + cIdx;
            rawOptions = apiOptions?.[k]?.items || apiOptions?.[k] || rawOptions;
          }
          const normalized = (Array.isArray(rawOptions) ? rawOptions : []).map((o) => {
            const optKey =
              (inp.optionKey && o && Object.prototype.hasOwnProperty.call(o, inp.optionKey) && o[inp.optionKey]) ||
              o?.label || o?.name || o?.key ||
              (o !== null && typeof o !== "object" ? String(o) : undefined) || "";
            const optValue =
              (inp.optionValue && o && Object.prototype.hasOwnProperty.call(o, inp.optionValue) && o[inp.optionValue]) ||
              o?.value || o?.id || o?.name || optKey || "";
            return { key: String(optKey), value: String(optValue) };
          });
          options.push({ key: inp.key, label: inp.label || inp.key, value: normalized, type: 'select', rIdx, cIdx });
          return;
        }

        // All other types (text, number, date, time, file) — no discrete options, use free-text match
        options.push({ key: inp.key, label: inp.label || inp.key, value: [], type: inp.type, rIdx, cIdx });
      });
    });
    setConditionOptions(options);
  }, [grid, apiOptions]);

  // --------- Drag listeners on the window for robustness ----------
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragState.active) return;
      const { clientX: x, clientY: y } = e;
      setDragState((prev) => ({ ...prev, pos: { x, y } }));

      const hit = findCellFromPoint(x, y);
      if (hit) setHoverCell(hit);
      else setHoverCell({ row: null, column: null });
    };

    const onMouseUp = () => {
      if (!dragState.active) return;
      // Drop
      if (
        hoverCell.row !== null &&
        hoverCell.column !== null &&
        grid[hoverCell.row] &&
        grid[hoverCell.row].columns[hoverCell.column]
      ) {
        const targetCell = grid[hoverCell.row].columns[hoverCell.column];
        if (!targetCell.input) {
          const newItem = deepClone(dragState.item);
          setGrid((prev) =>
            prev.map((r, rIdx) =>
              rIdx === hoverCell.row
                ? {
                    columns: r.columns.map((c, cIdx) =>
                      cIdx === hoverCell.column ? { input: newItem } : c,
                    ),
                  }
                : r,
            ),
          );
        }
      }
      // Reset drag
      setDragState({
        active: false,
        item: null,
        pos: { x: 0, y: 0 },
        start: { x: 0, y: 0 },
      });
      setHoverCell({ row: null, column: null });
    };

    if (dragState.active) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragState.active, dragState.item, hoverCell, grid]);

  // --------- Helpers ----------
  const findCellFromPoint = (x, y) => {
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[r].columns.length; c++) {
        const el = document.getElementById(`grid_${r}_${c}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (
          x >= rect.left &&
          x <= rect.right &&
          y >= rect.top &&
          y <= rect.bottom
        ) {
          return { row: r, column: c };
        }
      }
    }
    return null;
  };

  // Set date mode (single choice) for a date input: 'isCurrent' | 'isPast' | 'isFuture' | ''
  const handleDateModeChange = (rowIndex, columnIndex, mode) => {
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const nextInput = { ...c.input };
            nextInput.isCurrent = mode === "isCurrent";
            nextInput.isPast = mode === "isPast";
            nextInput.isFuture = mode === "isFuture";
            // ensure numeric fields exist
            if (nextInput.isPast && nextInput.pastDays === undefined) nextInput.pastDays = 0;
            if (nextInput.isFuture && nextInput.futureDays === undefined) nextInput.futureDays = 0;
            if (!nextInput.isPast) nextInput.pastDays = nextInput.pastDays ?? 0;
            if (!nextInput.isFuture) nextInput.futureDays = nextInput.futureDays ?? 0;
            return { input: nextInput };
          }),
        };
      }),
    );
  };

  const startDrag = (e, paletteIndex) => {
    e.preventDefault();
    const item = deepClone(PALETTE[paletteIndex]);
    setDragState({
      active: true,
      item,
      pos: { x: e.clientX, y: e.clientY },
      start: { x: e.clientX, y: e.clientY },
    });
  };

  // --------- Grid operations ----------
  const addGrid = () => setGrid((prev) => [...prev, emptyRow()]);
  const addColumn = (rowIndex) =>
    setGrid((prev) =>
      prev.map((r, i) =>
        i === rowIndex ? { columns: [...r.columns, emptyCell()] } : r,
      ),
    );
  const removeColumn = (rowIndex, columnIndex) =>
    setGrid((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? {
              columns: r.columns.filter((_, idx) => idx !== columnIndex),
            }
          : r,
      ),
    );
  const removeRow = (rowIndex) =>
    setGrid((prev) => prev.filter((_, idx) => idx !== rowIndex));

  const toggleRowAddMore = (rowIndex) =>
    setGrid((prev) =>
      prev.map((r, i) =>
        i === rowIndex ? { ...r, enableAddMore: !r.enableAddMore } : r,
      ),
    );
  const removeInput = (rowIndex, columnIndex) =>
    setGrid((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? {
              columns: r.columns.map((c, j) =>
                j === columnIndex ? emptyCell() : c,
              ),
            }
          : r,
      ),
    );

  const fetchAPI = async (rowIndex, columnIndex, key) => {
    const currentObjectData = grid[rowIndex]["columns"][columnIndex];
    console.log(currentObjectData, "currentObjectData");
    if (currentObjectData?.input?.API?.cURL) {
      const apiData = currentObjectData?.input?.API;

      const formatData = await fetchFormApi(apiData);

      // Update apiOptions state safely
      // Normalize to label/value for consistent rendering
      const mapped = Array.isArray(formatData)
        ? formatData.map((it) => ({
            label: it?.name ?? String(it ?? ""),
            value: it?.value ?? it?.name ?? String(it ?? ""),
            ...it,
          }))
        : [];

      setApiOptions((prev) => {
        return {
          [key + rowIndex + columnIndex]: mapped,
        };
      });

      // Also update the grid cell so inp.options contains fetched items
      setGrid((prev) =>
        prev.map((r, ri) =>
          ri !== rowIndex
            ? r
            : {
                columns: r.columns.map((c, ci) =>
                  ci !== columnIndex
                    ? c
                    : {
                        ...c,
                        input: {
                          ...(c.input || {}),
                          options: mapped,
                          optionKey: "label",
                          optionValue: "value",
                        },
                      },
                ),
              },
        ),
      );

    }
  };

  // Fetch available tables/schema list from server and store in the cell's API.schemaTables
  const loadSchemaTables = async (rowIndex, columnIndex) => {
    try {
      const res = await API.get("/form/getAllTable");
      const tables = Array.isArray(res.data) ? res.data : [];
      setGrid((prev) =>
        prev.map((r, ri) =>
          ri !== rowIndex
            ? r
            : {
                columns: r.columns.map((c, ci) =>
                  ci !== columnIndex
                    ? c
                    : {
                        ...c,
                        input: {
                          ...(c.input || {}),
                          API: {
                            ...(c.input?.API || {}),
                            schemaTables: tables,
                          },
                        },
                      },
                ),
              },
        ),
      );
    } catch (err) {
      console.error("loadSchemaTables error", err);
    }
  };

  // Given selected table and label/value columns, fetch data rows and store as options
  const fetchTableData = async (rowIndex, columnIndex, page = 1, limit = 500) => {
    try {
      const cell = grid[rowIndex]?.columns?.[columnIndex];
      const api = cell?.input?.API || {};
      const payload = {
        tableName: api.schemaTable,
        value: api.schemaValue,
        label: api.schemaLabel,
      };
      // require table and value column; label is optional (will fall back to value)
      if (!payload.tableName || !payload.value) return;
      if (!payload.label) payload.label = payload.value;

      // attach pagination params
      const res = await API.post("/form/getDataByTable", { ...payload, page, limit });
      // normalize response: support res.data being array or { items: [] } or { data: [] }
      const maybe = res?.data;
      const data = Array.isArray(maybe)
        ? maybe
        : Array.isArray(maybe?.items)
        ? maybe.items
        : Array.isArray(maybe?.data)
        ? maybe.data
        : [];

      // Ensure items are { label, value }
      const mapped = Array.isArray(data) ? data.map((it) => ({
        label: it.label ?? String(it?.[payload.label] ?? it?.[payload.value] ?? ""),
        value: it.value ?? String(it?.[payload.value] ?? it?.[payload.label] ?? ""),
        ...it,
      })) : [];
      const key = "schemaOptions" + rowIndex + columnIndex;
      setApiOptions((prev) => {
        const prevObj = prev?.[key] || { items: [], page: 0, hasMore: true };
        const newItems = page === 1 ? mapped : [...(prevObj.items || []), ...mapped];
        return {
          ...prev,
          [key]: { items: newItems, page: page, hasMore: mapped.length === limit },
        };
      });
      // Do NOT persist fetched options into the saved grid. Keep schema mapping
      // in input.API (schemaTable/schemaLabel/schemaValue) so the save payload
      // stores the schema reference instead of copying option values.
      setGrid((prev) =>
        prev.map((r, ri) =>
          ri !== rowIndex
            ? r
            : {
                columns: r.columns.map((c, ci) =>
                          ci !== columnIndex
                            ? c
                            : {
                                ...c,
                                input: {
                                  ...(c.input || {}),
                                  _schemaFetched: true,
                                },
                              },
                        ),
              },
        ),
      );
    } catch (err) {
      console.error("fetchTableData error", err, err?.response?.data || null);
    }
  };

  /**only use for API */
  const handleChangeAPI = (event, rowIndex, columnIndex, key) => {
    const targetVal =
      key === "required" ||
      key === "isPattern" ||
      key === "isCondition" ||
      key === "isAPI" ||
      key === "isCurrent" ||
      key === "isPast" ||
      key === "isFuture" ||
      key === "HEADER_PL" ||
      key === "HEADER_CMS" ||
      key === "HEADER_CUSTOM"
        ? event.target.checked
        :  key === 'payload' ? event.target.value :  (event.target.value?.replaceAll("  ", "") ?? "");

    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const nextInput = {
              ...c.input,
              API: {
                ...(c?.input?.API || {}),
                [key]: targetVal,
              },
            };

            // If user toggles 'useSchema' we mirror that into sourceType for UI
            if (key === "useSchema") {
              if (targetVal) nextInput.sourceType = "schema";
              else if (nextInput.sourceType === "schema") nextInput.sourceType = "";
            }

            // If user toggles isAPI explicitly, ensure other source flags are cleared
            if (key === "isAPI") {
              if (targetVal) {
                nextInput.isAPI = true;
                nextInput.isJson = false;
                nextInput.isSchema = false;
                nextInput.sourceType = "api";
              } else {
                nextInput.isAPI = false;
                if (nextInput.sourceType === "api") nextInput.sourceType = "";
              }
            }

            // ensure useSchema maps to isSchema flag as well
            if (key === "useSchema" && targetVal) {
              nextInput.isSchema = true;
              nextInput.isAPI = false;
              nextInput.isJson = false;
            }

            // Auto-generate key from label
            if (key === "label") {
              nextInput.key = nextInput.type === "file"
                ? "Upload_File_" + (targetVal || "").replaceAll(" ", "_")
                : (targetVal || "").replaceAll(" ", "_");
            }
            if (
              key === "required" ||
              key === "isPattern" ||
              key === "isCondition" ||
              key === "isAPI"
            ) {
              nextInput.tempOptions = [];
            }
            return { input: nextInput };
          }),
        };
      }),
    );
  };

  // --------- Input change handlers ----------
  const handleNoteHtmlChange = (value, rowIndex, columnIndex) => {
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            return { input: { ...c.input, noteHtml: value } };
          }),
        };
      }),
    );
  };

  const handleChange = (event, rowIndex, columnIndex, key) => {
    const targetVal =
      key === "required" ||
        key === "isPattern" ||
        key === "isCondition" ||
        key === "isAPI" ||
        key === "isCurrent" ||
        key === "isPast" ||
        key === "isFuture" ||
        key === "isRangeDropdown" ||
        key === "ifFilterEnable" ||
        key === "isNote" ||
        key === "isPreview"
        ? event.target.checked
        : (event.target.value?.replaceAll("  ", "") ?? "");

    // If switching source type we may need to clear cached api/schema previews
    if (key === "sourceType") {
      // remove any cached api/schema preview options for this cell
      setApiOptions((prev) => {
        const copy = { ...(prev || {}) };
        delete copy["apiOptions" + rowIndex + columnIndex];
        delete copy["schemaOptions" + rowIndex + columnIndex];
        return copy;
      });
    }

    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const nextInput = { ...c.input, [key]: targetVal };

            // Auto-generate key from label
            if (key === "label") {
              nextInput.key = nextInput.type === "file"
                ? "Upload_File_" + (targetVal || "").replaceAll(" ", "_")
                : (targetVal || "").replaceAll(" ", "_");
            }

            // When switching source type handle persistence/cleanup
            if (key === "sourceType") {
              // remove runtime-only markers left by API/schema fetches
              delete nextInput._apiFetched;
              delete nextInput._schemaFetched;

              // set mutually-exclusive flags for source
              nextInput.isAPI = targetVal === "api";
              nextInput.isJson = targetVal === "json";
              nextInput.isSchema = targetVal === "schema";

              // If switching to JSON source, and an uploaded JSON exists,
              // map it into `options` so it will be saved to the DB.
              if (targetVal === "json") {
                const uploaded = nextInput.uploadedJson || [];
                const labelKey = nextInput.jsonLabel || nextInput.API?.label || nextInput.optionKey || "label";
                const valueKey = nextInput.jsonValue || nextInput.API?.value || nextInput.optionValue || "value";
                if (Array.isArray(uploaded) && uploaded.length) {
                  nextInput.options = uploaded.map((item) => ({
                    label: (item?.[labelKey] ?? String(item) ?? "") + "",
                    value: (item?.[valueKey] ?? String(item) ?? "") + "",
                  }));
                  nextInput.optionKey = "label";
                  nextInput.optionValue = "value";
                } else {
                  // If no uploaded payload yet, ensure options is at least empty array
                  nextInput.options = nextInput.options || [];
                }
              } else if (targetVal === "api" || targetVal === "schema") {
                // switching away from JSON -> clear options so preview will fetch
                nextInput.options = [];
              }

              // Clear leftover state from other source types to avoid conflicts
              // API selected -> clear any JSON-related artifacts
              if (targetVal === "api") {
                delete nextInput.uploadedJson;
                delete nextInput.jsonTextRaw;
                delete nextInput.jsonTextError;
                delete nextInput.jsonLabel;
                delete nextInput.jsonValue;
                nextInput.API = nextInput.API || {};
              }

              // JSON or JSON Text selected -> clear API/schema artifacts
              if (targetVal === "json" || targetVal === "jsonText") {
                // clear API config so cURL/response mappings don't conflict
                nextInput.API = undefined;
                nextInput.isAPI = false;
                nextInput.isSchema = false;
              }

              // Schema selected -> clear JSON artifacts and ensure API.useSchema flag handled elsewhere
              if (targetVal === "schema") {
                delete nextInput.uploadedJson;
                delete nextInput.jsonTextRaw;
                delete nextInput.jsonTextError;
                delete nextInput.jsonLabel;
                delete nextInput.jsonValue;
                // keep nextInput.API so schema selection UI can populate API.schemaTables
                nextInput.API = nextInput.API || {};
              }
            }

            if (
              key === "required" ||
              key === "isPattern" ||
              key === "isCondition" ||
              key === "isAPI"
            ) {
              nextInput.tempOptions = [];
            }
            // Special handling for date mode options
            if (nextInput.type === "date") {
              if (key === "isCurrent" && nextInput.isCurrent) {
                // isCurrent overrides others
                nextInput.isPast = false;
                nextInput.isFuture = false;
              }
              if ((key === "isPast" || key === "isFuture") && nextInput[key]) {
                // selecting past/future disables current
                nextInput.isCurrent = false;
              }
              if (key === "pastDays") {
                nextInput.pastDays = Number(targetVal) || 0;
              }
              if (key === "futureDays") {
                nextInput.futureDays = Number(targetVal) || 0;
              }
            }
            return { input: nextInput };
          }),
        };
      }),
    );
  };

  const addOptions = (event, rowIndex, columnIndex) => {
    if (event.code !== "Enter") return;
    const val = (event.target.value || "").trim();
    if (!val) return;

    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const options = Array.isArray(c.input.options)
              ? [...c.input.options]
              : [];
            options.push({ key: val, value: val });

            const nextInput = {
              ...c.input,
              optionKey: "key",
              optionValue: "value",
              options,
            };
            return { input: nextInput };
          }),
        };
      }),
    );
    event.target.value = "";
  };

  const addConditions = (event, rowIndex, columnIndex, key) => {
    const idx = event.target.value;
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const nextInput = { ...c.input };
            if (!nextInput.conditionalRender) {
              nextInput.conditionalRender = {};
            }
            if (key === "condition-key") {
              const picked = conditionOptions[idx];
              if (picked) {
                nextInput.conditionalRender.key = picked.key;
                nextInput.conditionalRender.controllingType = picked.type || '';
                nextInput.tempOptions = picked.value;
                // Clear previous condition value when changing key
                nextInput.conditionalRender.value = '';
              }
            }
            if (key === "condition-value") {
              nextInput.conditionalRender.value = event.target.value;
            }
            return { input: nextInput };
          }),
        };
      }),
    );
  };

  const handleCheckboxOptions = (event, rowIndex, columnIndex) => {
    const val = event.target.value ?? "";
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const opts = c.input.checkboxOptions ?? [{ label: "", key: "" }];
            const lastIdx = opts.length - 1;
            const newOpts = [...opts];
            newOpts[lastIdx] = {
              label: val,
              key: val.toLowerCase().replaceAll(" ", "_"),
            };
            return { input: { ...c.input, checkboxOptions: newOpts } };
          }),
        };
      }),
    );
  };

  const addCheckboxOptions = (rowIndex, columnIndex) => {
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const opts = c.input.checkboxOptions ?? [];
            const last = opts[opts.length - 1] || { label: "", key: "" };
            if (!last.label || !last.key) return c; // do nothing if incomplete
            return {
              input: {
                ...c.input,
                checkboxOptions: [...opts, { label: "", key: "" }],
              },
            };
          }),
        };
      }),
    );
  };

  const handleRadioOptions = (event, rowIndex, columnIndex) => {
    const val = event.target.value ?? "";
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const opts = c.input.radioOptions ?? [{ label: "", key: "" }];
            const lastIdx = opts.length - 1;
            const newOpts = [...opts];
            newOpts[lastIdx] = {
              label: val,
              key: val.toLowerCase().replaceAll(" ", "_"),
            };
            return { input: { ...c.input, radioOptions: newOpts } };
          }),
        };
      }),
    );
  };

  const addRadioOptions = (rowIndex, columnIndex) => {
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const opts = c.input.radioOptions ?? [];
            const last = opts[opts.length - 1] || { label: "", key: "" };
            if (!last.label || !last.key) return c;
            return {
              input: {
                ...c.input,
                radioOptions: [...opts, { label: "", key: "" }],
              },
            };
          }),
        };
      }),
    );
  };

  const removeRadioOption = (rowIndex, columnIndex, optIndex) => {
    setGrid((prev) =>
      prev.map((r, ri) => {
        if (ri !== rowIndex) return r;
        return {
          columns: r.columns.map((c, ci) => {
            if (ci !== columnIndex) return c;
            if (!c.input) return c;
            const opts = (c.input.radioOptions ?? []).filter((_, i) => i !== optIndex);
            return { input: { ...c.input, radioOptions: opts } };
          }),
        };
      }),
    );
  };

  // --------- JSON mode ----------
  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  const handleJSONInput = (e) => {
    const val = e.target.value;
    setJSONValidator({ error: "" });
    setJSONDataStr(val);

    if (!isValidJSON(val)) {
      setJSONValidator({ error: "Invalid JSON format" });
      return;
    }
    const parsed = JSON.parse(val);
    if (!Array.isArray(parsed)) {
      setJSONValidator({ error: "JSON should be an array of items" });
      return;
    }
    for (const json of parsed) {
      if (
        !Object.prototype.hasOwnProperty.call(json, "image") ||
        !Object.prototype.hasOwnProperty.call(json, "title") ||
        !Object.prototype.hasOwnProperty.call(json, "description")
      ) {
        setJSONValidator({
          error: "One of these fields are missing: image | title | description",
        });
        return;
      }
    }
    setJson(parsed);
  };

  // --------- Drag reorder (rows and columns) ----------
  const onDragEnd = useCallback((result) => {
    const { type, source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    if (type === "ROW") {
      setGrid((prev) => {
        const next = Array.from(prev);
        const [removed] = next.splice(source.index, 1);
        next.splice(destination.index, 0, removed);
        return next;
      });
    } else if (type === "COL") {
      const srcRowIndex = parseInt(source.droppableId.split("-")[1], 10);
      const dstRowIndex = parseInt(destination.droppableId.split("-")[1], 10);
      setGrid((prev) => {
        const next = deepClone(prev);
        const srcCols = Array.from(next[srcRowIndex].columns);
        const [removed] = srcCols.splice(source.index, 1);
        if (srcRowIndex === dstRowIndex) {
          srcCols.splice(destination.index, 0, removed);
          next[srcRowIndex] = { columns: srcCols };
        } else {
          const dstCols = Array.from(next[dstRowIndex].columns);
          dstCols.splice(destination.index, 0, removed);
          next[srcRowIndex] = { columns: srcCols };
          next[dstRowIndex] = { columns: dstCols };
        }
        return next;
      });
    }
  }, []);

  // --------- Render ----------
  return (
    <div ref={containerRef}>
      <div className="container">
        <div className="d-flex justify-content-between">
          <div className="m-t-15 m-checkbox-inline custom-radio-ml">
            <div className="radio radio-primary">
              <Input
                id="radioinline1"
                type="radio"
                name="radio1"
                value="form"
                onChange={(e) => {
                  setFormType(e.target.value);
                  setType(e.target.value);
                }}
                defaultChecked
              />
              <Label className="mb-0" htmlFor="radioinline1">
                Form
              </Label>
            </div>
            <div className="radio radio-primary">
              <Input
                id="radioinline2"
                type="radio"
                name="radio1"
                onChange={(e) => {
                  setFormType(e.target.value);
                  setType(e.target.value);
                }}
                value="json"
              />
              <Label className="mb-0" htmlFor="radioinline2">
                JSON
              </Label>
            </div>
          </div>
        </div>

        {formType === "form" ? (
          <div className="row">
            {/* Grid area */}
            <div className="col-10 grid-container">
              <DragDropContext onDragEnd={onDragEnd}>
                <div className="container">
                  <Droppable droppableId="rows" type="ROW" direction="vertical">
                    {(rowsProvided) => (
                      <div
                        ref={rowsProvided.innerRef}
                        {...rowsProvided.droppableProps}
                      >
                        {grid.map((row, rowIndex) => (
                          <Draggable
                            key={`row-${rowIndex}`}
                            draggableId={`row-${rowIndex}`}
                            index={rowIndex}
                          >
                            {(rowDrag, rowSnap) => (
                              <div
                                ref={rowDrag.innerRef}
                                {...rowDrag.draggableProps}
                                className="row border py-2 mt-2"
                                style={{
                                  ...rowDrag.draggableProps.style,
                                  background: rowSnap.isDragging
                                    ? "#eef2ff"
                                    : undefined,
                                }}
                              >
                                <div className="d-flex align-items-start">
                                  {/* Row drag handle */}
                                  <div
                                    {...rowDrag.dragHandleProps}
                                    style={{
                                      cursor: "grab",
                                      padding: "0 6px",
                                      display: "flex",
                                      alignSelf: "center",
                                    }}
                                    title="Drag to reorder row"
                                  >
                                    <FeatherIcon
                                      icon="menu"
                                      size={16}
                                      className="text-muted"
                                    />
                                  </div>
                                  <Droppable
                                    droppableId={`cols-${rowIndex}`}
                                    type="COL"
                                    direction="horizontal"
                                  >
                                    {(colsProvided) => (
                                      <div
                                        ref={colsProvided.innerRef}
                                        {...colsProvided.droppableProps}
                                        className="d-flex"
                                        style={{ flex: 1 }}
                                      >
                                        {row?.columns.map(
                                          (column, columnIndex) => {
                                            const isHovered =
                                              hoverCell.row === rowIndex &&
                                              hoverCell.column === columnIndex;
                                            return (
                                              <Draggable
                                                key={`col-${rowIndex}-${columnIndex}`}
                                                draggableId={`col-${rowIndex}-${columnIndex}`}
                                                index={columnIndex}
                                              >
                                                {(colDrag) => (
                                                  <div
                                                    ref={colDrag.innerRef}
                                                    {...colDrag.draggableProps}
                                                    className={`col d-flex ms-2 ${
                                                      isHovered
                                                        ? "border border-primary rounded"
                                                        : ""
                                                    }`}
                                                    style={{ minHeight: 80 }}
                                                    id={`grid_${rowIndex}_${columnIndex}`}
                                                  >
                                                    {column?.input?.type ? (
                                                      <Card className="w-full border">
                                                        <CardHeader className="d-flex justify-content-between align-middle bg-light text-black custom-card-header">
                                                          <div className="d-flex align-items-center gap-1">
                                                            <span
                                                              {...colDrag.dragHandleProps}
                                                              style={{
                                                                cursor: "grab",
                                                                display: "flex",
                                                                alignItems:
                                                                  "center",
                                                              }}
                                                              title="Drag to reorder field"
                                                            >
                                                              <FeatherIcon
                                                                icon="move"
                                                                size={14}
                                                                className="text-muted"
                                                              />
                                                            </span>
                                                            <p className="ml-2 fw-bold mb-0">
                                                              {column?.input
                                                                ?.label?.length
                                                                ? column?.input
                                                                    ?.label
                                                                : (column?.input
                                                                    ?.name ??
                                                                  "")}
                                                            </p>
                                                          </div>
                                                          <div className="d-flex align-items-center gap-2">
                                                            <FeatherIcon
                                                              icon={
                                                                minimizedCells[
                                                                  `${rowIndex}-${columnIndex}`
                                                                ]
                                                                  ? "chevron-up"
                                                                  : "chevron-down"
                                                              }
                                                              className="text-black cursor-pointer"
                                                              title={
                                                                minimizedCells[
                                                                  `${rowIndex}-${columnIndex}`
                                                                ]
                                                                  ? "Minimize"
                                                                  : "Expand"
                                                              }
                                                              onClick={() =>
                                                                toggleMinimize(
                                                                  rowIndex,
                                                                  columnIndex,
                                                                )
                                                              }
                                                            />
                                                            <FeatherIcon
                                                              icon="x"
                                                              fill="black"
                                                              className="text-black cursor-pointer"
                                                              onClick={() =>
                                                                removeInput(
                                                                  rowIndex,
                                                                  columnIndex,
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                        </CardHeader>
                                                        {minimizedCells[
                                                          `${rowIndex}-${columnIndex}`
                                                        ] && (
                                                          <CardBody>
                                                            {column?.input?.type !== "note" && (
                                                              <>
                                                            <div className="mt-2">
                                                              <Label>
                                                                Label
                                                              </Label>
                                                              <Input
                                                                type="text"
                                                                value={
                                                                  column?.input
                                                                    ?.label ??
                                                                  ""
                                                                }
                                                                onChange={(
                                                                  event,
                                                                ) =>
                                                                  handleChange(
                                                                    event,
                                                                    rowIndex,
                                                                    columnIndex,
                                                                    "label",
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                            <div className="mt-2">
                                                              <Label>Key</Label>
                                                              <Input
                                                                type="text"
                                                                className="mt-2"
                                                                value={
                                                                  column?.input
                                                                    ?.key ?? ""
                                                                }
                                                                disabled={
                                                                  column?.input
                                                                    ?.type ===
                                                                  "file"
                                                                }
                                                                onChange={(
                                                                  event,
                                                                ) =>
                                                                  handleChange(
                                                                    event,
                                                                    rowIndex,
                                                                    columnIndex,
                                                                    "key",
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                              </>
                                                            )}

                                                            {/* File Upload Options */}
                                                            {column?.input
                                                              ?.type ===
                                                              "file" && (
                                                              <div className="mt-2">
                                                                <Label>
                                                                  Enable File
                                                                  Types
                                                                </Label>
                                                                <Select
                                                                  isMulti
                                                                  closeMenuOnSelect={
                                                                    false
                                                                  }
                                                                  menuPortalTarget={
                                                                    typeof document !==
                                                                    "undefined"
                                                                      ? document.body
                                                                      : null
                                                                  }
                                                                  menuPosition="fixed"
                                                                  value={(
                                                                    column
                                                                      ?.input
                                                                      ?.allowedFileTypes ||
                                                                    []
                                                                  )
                                                                    .map(
                                                                      (val) =>
                                                                        FILE_TYPE_OPTIONS.find(
                                                                          (
                                                                            opt,
                                                                          ) =>
                                                                            opt.value ===
                                                                            val,
                                                                        ),
                                                                    )
                                                                    .filter(
                                                                      Boolean,
                                                                    )}
                                                                  options={
                                                                    FILE_TYPE_OPTIONS
                                                                  }
                                                                  onChange={(
                                                                    selected,
                                                                  ) => {
                                                                    const options =
                                                                      selected
                                                                        ? selected.map(
                                                                            (
                                                                              opt,
                                                                            ) =>
                                                                              opt.value,
                                                                          )
                                                                        : [];
                                                                    setGrid(
                                                                      (prev) =>
                                                                        prev.map(
                                                                          (
                                                                            r,
                                                                            ri,
                                                                          ) =>
                                                                            ri !==
                                                                            rowIndex
                                                                              ? r
                                                                              : {
                                                                                  columns:
                                                                                    r.columns.map(
                                                                                      (
                                                                                        c,
                                                                                        ci,
                                                                                      ) =>
                                                                                        ci !==
                                                                                        columnIndex
                                                                                          ? c
                                                                                          : {
                                                                                              ...c,
                                                                                              input:
                                                                                                {
                                                                                                  ...c.input,
                                                                                                  allowedFileTypes:
                                                                                                    options,
                                                                                                },
                                                                                            },
                                                                                    ),
                                                                                },
                                                                        ),
                                                                    );
                                                                  }}
                                                                  classNamePrefix="react-select"
                                                                  placeholder="Select file types..."
                                                                  styles={{
                                                                    control: (
                                                                      base,
                                                                    ) => ({
                                                                      ...base,
                                                                      minHeight: 32,
                                                                      borderColor:
                                                                        "#ced4da",
                                                                      boxShadow:
                                                                        "none",
                                                                    }),
                                                                    multiValue:
                                                                      (
                                                                        base,
                                                                      ) => ({
                                                                        ...base,
                                                                        background:
                                                                          "#e9ecef",
                                                                        color:
                                                                          "#495057",
                                                                      }),
                                                                    option: (
                                                                      base,
                                                                      state,
                                                                    ) => ({
                                                                      ...base,
                                                                      color:
                                                                        "#495057",
                                                                      background:
                                                                        state.isFocused
                                                                          ? "#f8f9fa"
                                                                          : "#fff",
                                                                    }),
                                                                    menuPortal:
                                                                      (
                                                                        base,
                                                                      ) => ({
                                                                        ...base,
                                                                        zIndex: 99999,
                                                                      }),
                                                                    menu: (
                                                                      base,
                                                                    ) => ({
                                                                      ...base,
                                                                      zIndex: 99999,
                                                                    }),
                                                                  }}
                                                                />
                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Max File
                                                                    Size (MB)
                                                                  </Label>
                                                                  <Input
                                                                    type="number"
                                                                    min={0}
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.maxFileSize
                                                                        ? column
                                                                            .input
                                                                            .maxFileSize /
                                                                          1024 /
                                                                          1024
                                                                        : ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const val =
                                                                        parseInt(
                                                                          e
                                                                            .target
                                                                            .value,
                                                                          10,
                                                                        ) || 0;
                                                                      setGrid(
                                                                        (
                                                                          prev,
                                                                        ) =>
                                                                          prev.map(
                                                                            (
                                                                              r,
                                                                              ri,
                                                                            ) =>
                                                                              ri !==
                                                                              rowIndex
                                                                                ? r
                                                                                : {
                                                                                    columns:
                                                                                      r.columns.map(
                                                                                        (
                                                                                          c,
                                                                                          ci,
                                                                                        ) =>
                                                                                          ci !==
                                                                                          columnIndex
                                                                                            ? c
                                                                                            : {
                                                                                                ...c,
                                                                                                input:
                                                                                                  {
                                                                                                    ...c.input,
                                                                                                    maxFileSize:
                                                                                                      val *
                                                                                                      1024 *
                                                                                                      1024,
                                                                                                  },
                                                                                              },
                                                                                      ),
                                                                                  },
                                                                          ),
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>
                                                              </div>
                                                            )}
                                                            {column?.input?.type !== "note" && (
                                                            <div className="mt-2">
                                                              <Label>
                                                                Placeholder
                                                              </Label>
                                                              <Input
                                                                type="text"
                                                                value={
                                                                  column?.input
                                                                    ?.placeholder ??
                                                                  ""
                                                                }
                                                                onChange={(
                                                                  event,
                                                                ) =>
                                                                  handleChange(
                                                                    event,
                                                                    rowIndex,
                                                                    columnIndex,
                                                                    "placeholder",
                                                                  )
                                                                }
                                                              />
                                                            </div>
                                                            )}

                                                            {/* Required / Pattern / Conditional */}
                                                            <div className="d-flex flex-wrap mt-2 align-middle gap-3">
                                                              {column?.input?.type !== "note" && (
                                                                <div className="d-flex align-items-center gap-2">
                                                                  <Input
                                                                    className="checkbox_animated"
                                                                    type="checkbox"
                                                                    checked={
                                                                      !!column
                                                                        ?.input
                                                                        ?.required
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      handleChange(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "required",
                                                                      )
                                                                    }
                                                                  />
                                                                  <p className="fw-bold mb-0">
                                                                    Required
                                                                  </p>
                                                                </div>
                                                              )}
                                                              {column?.input?.type !== "note" && (
                                                                <div className="d-flex align-items-center gap-2">
                                                                  <Input
                                                                    className="checkbox_animated"
                                                                    type="checkbox"
                                                                    checked={
                                                                      !!column
                                                                        ?.input
                                                                        ?.isPattern
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      handleChange(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "isPattern",
                                                                      )
                                                                    }
                                                                  />
                                                                  <p className="fw-bold mb-0">
                                                                    Pattern
                                                                  </p>
                                                                </div>
                                                              )}
                                                              <div className="d-flex align-items-center gap-2">
                                                                <Input
                                                                  className="checkbox_animated"
                                                                  type="checkbox"
                                                                  checked={
                                                                    !!column
                                                                      ?.input
                                                                      ?.isCondition
                                                                  }
                                                                  onChange={(
                                                                    event,
                                                                  ) =>
                                                                    handleChange(
                                                                      event,
                                                                      rowIndex,
                                                                      columnIndex,
                                                                      "isCondition",
                                                                    )
                                                                  }
                                                                />
                                                                <p className="fw-bold mb-0">
                                                                  Conditional
                                                                </p>
                                                              </div>
                                                              {column?.input?.type !== "note" && (
                                                              <div className="d-flex align-items-center gap-2">
                                                                <Input
                                                                  className="checkbox_animated"
                                                                  type="checkbox"
                                                                  checked={
                                                                      !!(
                                                                        column
                                                                          ?.input
                                                                          ?.isPreview
                                                                      )
                                                                    }
                                                                  onChange={(
                                                                    event,
                                                                  ) =>
                                                                    handleChange(
                                                                      event,
                                                                      rowIndex,
                                                                      columnIndex,
                                                                      "isPreview",
                                                                    )
                                                                  }
                                                                />
                                                                <p className="fw-bold mb-0">
                                                                  Note
                                                                </p>
                                                              </div>
                                                              )}
                                                              {column?.input
                                                                ?.type ===
                                                                "select" && (
                                                                <div className="d-flex align-items-center gap-3">
                                                                  <p className="fw-bold mb-0">
                                                                    Source
                                                                  </p>
                                                                  <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                      type="radio"
                                                                      name={`source-${rowIndex}-${columnIndex}`}
                                                                      value="api"
                                                                      checked={
                                                                        column
                                                                          ?.input
                                                                          ?.sourceType ===
                                                                          "api" ||
                                                                        !!column
                                                                          ?.input
                                                                          ?.isAPI
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "sourceType",
                                                                        )
                                                                      }
                                                                    />
                                                                    <p className="fw-bold mb-0">
                                                                      API
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                      type="radio"
                                                                      name={`source-${rowIndex}-${columnIndex}`}
                                                                      value="json"
                                                                      checked={
                                                                        column
                                                                          ?.input
                                                                          ?.sourceType ===
                                                                        "json"
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "sourceType",
                                                                        )
                                                                      }
                                                                    />
                                                                    <p className="fw-bold mb-0">
                                                                      Upload
                                                                      JSON
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                      type="radio"
                                                                      name={`source-${rowIndex}-${columnIndex}`}
                                                                      value="jsonText"
                                                                      checked={
                                                                        column
                                                                          ?.input
                                                                          ?.sourceType ===
                                                                        "jsonText"
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "sourceType",
                                                                        )
                                                                      }
                                                                    />
                                                                    <p className="fw-bold mb-0">
                                                                      JSON Text
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                      type="radio"
                                                                      name={`source-${rowIndex}-${columnIndex}`}
                                                                      value="schema"
                                                                      checked={
                                                                        column
                                                                          ?.input
                                                                          ?.sourceType ===
                                                                        "schema"
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "sourceType",
                                                                        )
                                                                      }
                                                                    />
                                                                    <p className="fw-bold mb-0">
                                                                      DB Schema
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                              )}
                                                            </div>

                                                            {/* Date Mode Controls */}
                                                            {column?.input
                                                              ?.type ===
                                                              "date" && (
                                                              <div className="mt-2">
                                                                <Label>
                                                                  Date Mode
                                                                </Label>
                                                                <Input
                                                                  type="select"
                                                                  value={
                                                                    column
                                                                      ?.input
                                                                      ?.isCurrent
                                                                      ? "isCurrent"
                                                                      : column
                                                                            ?.input
                                                                            ?.isPast
                                                                        ? "isPast"
                                                                        : column
                                                                              ?.input
                                                                              ?.isFuture
                                                                          ? "isFuture"
                                                                          : ""
                                                                  }
                                                                  onChange={(
                                                                    e,
                                                                  ) =>
                                                                    handleDateModeChange(
                                                                      rowIndex,
                                                                      columnIndex,
                                                                      e.target
                                                                        .value,
                                                                    )
                                                                  }
                                                                >
                                                                  <option value="">
                                                                    -- None --
                                                                  </option>
                                                                  <option value="isCurrent">
                                                                    Current Date
                                                                  </option>
                                                                  <option value="isPast">
                                                                    Past
                                                                  </option>
                                                                  <option value="isFuture">
                                                                    Future
                                                                  </option>
                                                                </Input>

                                                                {column?.input
                                                                  ?.isPast && (
                                                                  <div className="d-flex align-items-center gap-2 mt-2">
                                                                    <Label className="mb-0">
                                                                      Past Days
                                                                    </Label>
                                                                    <Input
                                                                      type="number"
                                                                      min={0}
                                                                      value={
                                                                        column
                                                                          ?.input
                                                                          ?.pastDays ??
                                                                        0
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "pastDays",
                                                                        )
                                                                      }
                                                                      style={{
                                                                        width: 100,
                                                                      }}
                                                                    />
                                                                  </div>
                                                                )}

                                                                {column?.input
                                                                  ?.isFuture && (
                                                                  <div className="d-flex align-items-center gap-2 mt-2">
                                                                    <Label className="mb-0">
                                                                      Future
                                                                      Days
                                                                    </Label>
                                                                    <Input
                                                                      type="number"
                                                                      min={0}
                                                                      value={
                                                                        column
                                                                          ?.input
                                                                          ?.futureDays ??
                                                                        0
                                                                      }
                                                                      onChange={(
                                                                        event,
                                                                      ) =>
                                                                        handleChange(
                                                                          event,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "futureDays",
                                                                        )
                                                                      }
                                                                      style={{
                                                                        width: 100,
                                                                      }}
                                                                    />
                                                                  </div>
                                                                )}
                                                              </div>
                                                            )}

                                                            {/* Number Range Dropdown */}
                                                            {column?.input
                                                              ?.type ===
                                                              "number" && (
                                                              <div className="mt-2">
                                                                <div className="d-flex align-items-center gap-2">
                                                                  <Input
                                                                    className="checkbox_animated"
                                                                    type="checkbox"
                                                                    checked={
                                                                      !!column
                                                                        ?.input
                                                                        ?.isRangeDropdown
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      handleChange(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "isRangeDropdown",
                                                                      )
                                                                    }
                                                                  />
                                                                  <Label className="mb-0 fw-bold">
                                                                    Range
                                                                    Dropdown
                                                                  </Label>
                                                                </div>
                                                                {column?.input
                                                                  ?.isRangeDropdown && (
                                                                  <div className="d-flex align-items-center gap-3 mt-2">
                                                                    <div className="d-flex align-items-center gap-1">
                                                                      <Label className="mb-0">
                                                                        Min
                                                                      </Label>
                                                                      <Input
                                                                        type="number"
                                                                        value={
                                                                          column
                                                                            ?.input
                                                                            ?.rangeMin ??
                                                                          0
                                                                        }
                                                                        onChange={(
                                                                          event,
                                                                        ) =>
                                                                          handleChange(
                                                                            event,
                                                                            rowIndex,
                                                                            columnIndex,
                                                                            "rangeMin",
                                                                          )
                                                                        }
                                                                        style={{
                                                                          width: 80,
                                                                        }}
                                                                      />
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-1">
                                                                      <Label className="mb-0">
                                                                        Max
                                                                      </Label>
                                                                      <Input
                                                                        type="number"
                                                                        value={
                                                                          column
                                                                            ?.input
                                                                            ?.rangeMax ??
                                                                          10
                                                                        }
                                                                        onChange={(
                                                                          event,
                                                                        ) =>
                                                                          handleChange(
                                                                            event,
                                                                            rowIndex,
                                                                            columnIndex,
                                                                            "rangeMax",
                                                                          )
                                                                        }
                                                                        style={{
                                                                          width: 80,
                                                                        }}
                                                                      />
                                                                    </div>
                                                                  </div>
                                                                )}
                                                              </div>
                                                            )}

                                                            {/* Patterns */}
                                                            {column?.input
                                                              ?.isPattern &&
                                                            (column?.input
                                                              ?.type ===
                                                              "text" ||
                                                              column?.input
                                                                ?.type ===
                                                                "number") ? (
                                                              <div className="mt-1">
                                                                <Label>
                                                                  Patterns
                                                                </Label>
                                                                <Input
                                                                  className="w-full"
                                                                  type="select"
                                                                  value={
                                                                    column
                                                                      ?.input
                                                                      ?.pattern ??
                                                                    ""
                                                                  }
                                                                  onChange={(
                                                                    event,
                                                                  ) =>
                                                                    handleChange(
                                                                      event,
                                                                      rowIndex,
                                                                      columnIndex,
                                                                      "pattern",
                                                                    )
                                                                  }
                                                                >
                                                                  <option value="">
                                                                    -- Select --
                                                                  </option>
                                                                  {PATTERNS.map(
                                                                    (
                                                                      opt,
                                                                      idx,
                                                                    ) => (
                                                                      <option
                                                                        key={
                                                                          idx
                                                                        }
                                                                        value={
                                                                          opt.value
                                                                        }
                                                                      >
                                                                        {
                                                                          opt.name
                                                                        }
                                                                      </option>
                                                                    ),
                                                                  )}
                                                                </Input>
                                                              </div>
                                                            ) : null}

                                                            {/* Schema-driven source for select fields */}
                                                            {column?.input &&
                                                            column?.input
                                                              ?.type ===
                                                              "select" &&
                                                            column?.input
                                                              ?.sourceType ===
                                                              "schema" ? (
                                                              <div className="mt-2">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                  <Label>
                                                                    DB Schema
                                                                  </Label>
                                                                  <div>
                                                                    <Button
                                                                      color="secondary"
                                                                      size="sm"
                                                                      outline
                                                                      onClick={() =>
                                                                        loadSchemaTables(
                                                                          rowIndex,
                                                                          columnIndex,
                                                                        )
                                                                      }
                                                                    >
                                                                      Load
                                                                      Schemas
                                                                    </Button>
                                                                  </div>
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Select Table
                                                                  </Label>
                                                                  <Input
                                                                    type="select"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.schemaTable ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) =>
                                                                      handleChangeAPI(
                                                                        e,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "schemaTable",
                                                                      )
                                                                    }
                                                                  >
                                                                    <option value="">
                                                                      -- Select
                                                                      table --
                                                                    </option>
                                                                    {(
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.schemaTables ||
                                                                      []
                                                                    ).map(
                                                                      (
                                                                        t,
                                                                        idx,
                                                                      ) => (
                                                                        <option
                                                                          key={
                                                                            idx
                                                                          }
                                                                          value={
                                                                            t.tableName
                                                                          }
                                                                        >
                                                                          {
                                                                            t.tableName
                                                                          }
                                                                        </option>
                                                                      ),
                                                                    )}
                                                                  </Input>
                                                                </div>

                                                                <div className="mt-2 d-flex gap-2">
                                                                  <div
                                                                    style={{
                                                                      flex: 1,
                                                                    }}
                                                                  >
                                                                    <Label>
                                                                      Label
                                                                      Column
                                                                    </Label>
                                                                    <Input
                                                                      type="select"
                                                                      value={
                                                                        column
                                                                          ?.input
                                                                          ?.API
                                                                          ?.schemaLabel ||
                                                                        ""
                                                                      }
                                                                      onChange={(
                                                                        e,
                                                                      ) =>
                                                                        handleChangeAPI(
                                                                          e,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "schemaLabel",
                                                                        )
                                                                      }
                                                                    >
                                                                      <option value="">
                                                                        --
                                                                        Select
                                                                        column
                                                                        --
                                                                      </option>
                                                                      {(() => {
                                                                        const tables =
                                                                          column
                                                                            ?.input
                                                                            ?.API
                                                                            ?.schemaTables ||
                                                                          [];
                                                                        const sel =
                                                                          column
                                                                            ?.input
                                                                            ?.API
                                                                            ?.schemaTable;
                                                                        const found =
                                                                          tables.find(
                                                                            (
                                                                              x,
                                                                            ) =>
                                                                              x.tableName ===
                                                                              sel,
                                                                          );
                                                                        return (
                                                                          found?.columns ||
                                                                          []
                                                                        );
                                                                      })().map(
                                                                        (
                                                                          colName,
                                                                          i,
                                                                        ) => (
                                                                          <option
                                                                            key={
                                                                              i
                                                                            }
                                                                            value={
                                                                              colName
                                                                            }
                                                                          >
                                                                            {
                                                                              colName
                                                                            }
                                                                          </option>
                                                                        ),
                                                                      )}
                                                                    </Input>
                                                                  </div>
                                                                  <div
                                                                    style={{
                                                                      flex: 1,
                                                                    }}
                                                                  >
                                                                    <Label>
                                                                      Value
                                                                      Column
                                                                    </Label>
                                                                    <Input
                                                                      type="select"
                                                                      value={
                                                                        column
                                                                          ?.input
                                                                          ?.API
                                                                          ?.schemaValue ||
                                                                        ""
                                                                      }
                                                                      onChange={(
                                                                        e,
                                                                      ) =>
                                                                        handleChangeAPI(
                                                                          e,
                                                                          rowIndex,
                                                                          columnIndex,
                                                                          "schemaValue",
                                                                        )
                                                                      }
                                                                    >
                                                                      <option value="">
                                                                        --
                                                                        Select
                                                                        column
                                                                        --
                                                                      </option>
                                                                      {(() => {
                                                                        const tables =
                                                                          column
                                                                            ?.input
                                                                            ?.API
                                                                            ?.schemaTables ||
                                                                          [];
                                                                        const sel =
                                                                          column
                                                                            ?.input
                                                                            ?.API
                                                                            ?.schemaTable;
                                                                        const found =
                                                                          tables.find(
                                                                            (
                                                                              x,
                                                                            ) =>
                                                                              x.tableName ===
                                                                              sel,
                                                                          );
                                                                        return (
                                                                          found?.columns ||
                                                                          []
                                                                        );
                                                                      })().map(
                                                                        (
                                                                          colName,
                                                                          i,
                                                                        ) => (
                                                                          <option
                                                                            key={
                                                                              i
                                                                            }
                                                                            value={
                                                                              colName
                                                                            }
                                                                          >
                                                                            {
                                                                              colName
                                                                            }
                                                                          </option>
                                                                        ),
                                                                      )}
                                                                    </Input>
                                                                  </div>
                                                                </div>

                                                                <div className="mt-2 d-flex justify-content-between align-items-center">
                                                                  <div>
                                                                    <Label>
                                                                      Fetch
                                                                      Table
                                                                      Options
                                                                    </Label>
                                                                  </div>
                                                                  <div>
                                                                    <Button
                                                                      color="primary"
                                                                      size="sm"
                                                                      outline
                                                                      onClick={() =>
                                                                        fetchTableData(
                                                                          rowIndex,
                                                                          columnIndex,
                                                                        )
                                                                      }
                                                                    >
                                                                      Fetch
                                                                      Table Data
                                                                    </Button>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* JSON Text source for select fields */}
                                                            {column?.input &&
                                                            column?.input
                                                              ?.type ===
                                                              "select" &&
                                                            column?.input
                                                              ?.sourceType ===
                                                              "jsonText" ? (
                                                              <div className="mt-2">
                                                                <Label>
                                                                  Paste JSON
                                                                  Array
                                                                </Label>
                                                                <Input
                                                                  type="textarea"
                                                                  style={{
                                                                    minHeight: 120,
                                                                    fontFamily:
                                                                      "monospace",
                                                                    fontSize: 12,
                                                                  }}
                                                                  placeholder={
                                                                    '[{"label": "Option 1", "value": "1"}, ...]'
                                                                  }
                                                                  value={
                                                                    column
                                                                      ?.input
                                                                      ?.jsonTextRaw ||
                                                                    ""
                                                                  }
                                                                  onChange={(
                                                                    e,
                                                                  ) => {
                                                                    const raw =
                                                                      e.target
                                                                        .value;
                                                                    setGrid(
                                                                      (prev) =>
                                                                        prev.map(
                                                                          (
                                                                            r,
                                                                            ri,
                                                                          ) => {
                                                                            if (
                                                                              ri !==
                                                                              rowIndex
                                                                            )
                                                                              return r;
                                                                            return {
                                                                              columns:
                                                                                r.columns.map(
                                                                                  (
                                                                                    c,
                                                                                    ci,
                                                                                  ) => {
                                                                                    if (
                                                                                      ci !==
                                                                                      columnIndex
                                                                                    )
                                                                                      return c;
                                                                                    let arr =
                                                                                      [];
                                                                                    let parseError =
                                                                                      "";
                                                                                    try {
                                                                                      const parsed =
                                                                                        JSON.parse(
                                                                                          raw,
                                                                                        );
                                                                                      arr =
                                                                                        Array.isArray(
                                                                                          parsed,
                                                                                        )
                                                                                          ? parsed
                                                                                          : Array.isArray(
                                                                                                parsed?.data,
                                                                                              )
                                                                                            ? parsed.data
                                                                                            : [];
                                                                                    } catch {
                                                                                      parseError =
                                                                                        "Invalid JSON";
                                                                                    }
                                                                                    const labelKey =
                                                                                      c
                                                                                        .input
                                                                                        ?.jsonLabel ||
                                                                                      "label";
                                                                                    const valueKey =
                                                                                      c
                                                                                        .input
                                                                                        ?.jsonValue ||
                                                                                      "value";
                                                                                    const options =
                                                                                      arr.map(
                                                                                        (
                                                                                          item,
                                                                                        ) => ({
                                                                                          label:
                                                                                            (item?.[
                                                                                              labelKey
                                                                                            ] ??
                                                                                              String(
                                                                                                item,
                                                                                              )) +
                                                                                            "",
                                                                                          value:
                                                                                            (item?.[
                                                                                              valueKey
                                                                                            ] ??
                                                                                              String(
                                                                                                item,
                                                                                              )) +
                                                                                            "",
                                                                                        }),
                                                                                      );
                                                                                    return {
                                                                                      ...c,
                                                                                      input:
                                                                                        {
                                                                                          ...c.input,
                                                                                          jsonTextRaw:
                                                                                            raw,
                                                                                          jsonTextError:
                                                                                            parseError,
                                                                                          uploadedJson:
                                                                                            arr,
                                                                                          options,
                                                                                        },
                                                                                    };
                                                                                  },
                                                                                ),
                                                                            };
                                                                          },
                                                                        ),
                                                                    );
                                                                  }}
                                                                />
                                                                {column?.input
                                                                  ?.jsonTextError && (
                                                                  <div
                                                                    className="text-danger mt-1"
                                                                    style={{
                                                                      fontSize: 12,
                                                                    }}
                                                                  >
                                                                    {
                                                                      column
                                                                        .input
                                                                        .jsonTextError
                                                                    }
                                                                  </div>
                                                                )}

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Label Key
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    placeholder="e.g. fruit"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.jsonLabel ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const val =
                                                                        e.target
                                                                          .value;
                                                                      setGrid(
                                                                        (
                                                                          prev,
                                                                        ) =>
                                                                          prev.map(
                                                                            (
                                                                              r,
                                                                              ri,
                                                                            ) => {
                                                                              if (
                                                                                ri !==
                                                                                rowIndex
                                                                              )
                                                                                return r;
                                                                              return {
                                                                                columns:
                                                                                  r.columns.map(
                                                                                    (
                                                                                      c,
                                                                                      ci,
                                                                                    ) => {
                                                                                      if (
                                                                                        ci !==
                                                                                        columnIndex
                                                                                      )
                                                                                        return c;
                                                                                      const uploaded =
                                                                                        c
                                                                                          .input
                                                                                          ?.uploadedJson ||
                                                                                        [];
                                                                                      const valueKey =
                                                                                        c
                                                                                          .input
                                                                                          ?.jsonValue ||
                                                                                        "value";
                                                                                      return {
                                                                                        ...c,
                                                                                        input:
                                                                                          {
                                                                                            ...c.input,
                                                                                            jsonLabel:
                                                                                              val,
                                                                                            options:
                                                                                              uploaded.map(
                                                                                                (
                                                                                                  item,
                                                                                                ) => ({
                                                                                                  label:
                                                                                                    (item?.[
                                                                                                      val
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                  value:
                                                                                                    (item?.[
                                                                                                      valueKey
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                }),
                                                                                              ),
                                                                                          },
                                                                                      };
                                                                                    },
                                                                                  ),
                                                                              };
                                                                            },
                                                                          ),
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Value Key
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    placeholder="e.g. color"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.jsonValue ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const val =
                                                                        e.target
                                                                          .value;
                                                                      setGrid(
                                                                        (
                                                                          prev,
                                                                        ) =>
                                                                          prev.map(
                                                                            (
                                                                              r,
                                                                              ri,
                                                                            ) => {
                                                                              if (
                                                                                ri !==
                                                                                rowIndex
                                                                              )
                                                                                return r;
                                                                              return {
                                                                                columns:
                                                                                  r.columns.map(
                                                                                    (
                                                                                      c,
                                                                                      ci,
                                                                                    ) => {
                                                                                      if (
                                                                                        ci !==
                                                                                        columnIndex
                                                                                      )
                                                                                        return c;
                                                                                      const uploaded =
                                                                                        c
                                                                                          .input
                                                                                          ?.uploadedJson ||
                                                                                        [];
                                                                                      const labelKey =
                                                                                        c
                                                                                          .input
                                                                                          ?.jsonLabel ||
                                                                                        "label";
                                                                                      return {
                                                                                        ...c,
                                                                                        input:
                                                                                          {
                                                                                            ...c.input,
                                                                                            jsonValue:
                                                                                              val,
                                                                                            options:
                                                                                              uploaded.map(
                                                                                                (
                                                                                                  item,
                                                                                                ) => ({
                                                                                                  label:
                                                                                                    (item?.[
                                                                                                      labelKey
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                  value:
                                                                                                    (item?.[
                                                                                                      val
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                }),
                                                                                              ),
                                                                                          },
                                                                                      };
                                                                                    },
                                                                                  ),
                                                                              };
                                                                            },
                                                                          ),
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* JSON upload source for select fields (moved here) */}
                                                            {column?.input &&
                                                            column?.input
                                                              ?.type ===
                                                              "select" &&
                                                            column?.input
                                                              ?.sourceType ===
                                                              "json" ? (
                                                              <div className="mt-2">
                                                                <div>
                                                                  <Label>
                                                                    Upload JSON
                                                                    File (Array
                                                                    of objects)
                                                                  </Label>
                                                                  <Input
                                                                    type="file"
                                                                    accept=".json,application/json"
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const file =
                                                                        e.target
                                                                          .files?.[0];
                                                                      if (!file)
                                                                        return;
                                                                      const reader =
                                                                        new FileReader();
                                                                      reader.onload =
                                                                        (
                                                                          ev,
                                                                        ) => {
                                                                          try {
                                                                            const parsed =
                                                                              JSON.parse(
                                                                                ev
                                                                                  .target
                                                                                  .result,
                                                                              );
                                                                            const arr =
                                                                              Array.isArray(
                                                                                parsed,
                                                                              )
                                                                                ? parsed
                                                                                : Array.isArray(
                                                                                      parsed?.data,
                                                                                    )
                                                                                  ? parsed.data
                                                                                  : [];
                                                                            const labelKey =
                                                                              column
                                                                                ?.input
                                                                                ?.jsonLabel ||
                                                                              column
                                                                                ?.input
                                                                                ?.API
                                                                                ?.label ||
                                                                              "label";
                                                                            const valueKey =
                                                                              column
                                                                                ?.input
                                                                                ?.jsonValue ||
                                                                              column
                                                                                ?.input
                                                                                ?.API
                                                                                ?.value ||
                                                                              "value";
                                                                            const options =
                                                                              arr.map(
                                                                                (
                                                                                  item,
                                                                                ) => ({
                                                                                  label:
                                                                                    (item?.[
                                                                                      labelKey
                                                                                    ] ??
                                                                                      String(
                                                                                        item,
                                                                                      )) +
                                                                                    "",
                                                                                  value:
                                                                                    (item?.[
                                                                                      valueKey
                                                                                    ] ??
                                                                                      String(
                                                                                        item,
                                                                                      )) +
                                                                                    "",
                                                                                }),
                                                                              );

                                                                            setGrid(
                                                                              (
                                                                                prev,
                                                                              ) =>
                                                                                prev.map(
                                                                                  (
                                                                                    r,
                                                                                    ri,
                                                                                  ) => {
                                                                                    if (
                                                                                      ri !==
                                                                                      rowIndex
                                                                                    )
                                                                                      return r;
                                                                                    return {
                                                                                      columns:
                                                                                        r.columns.map(
                                                                                          (
                                                                                            c,
                                                                                            ci,
                                                                                          ) =>
                                                                                            ci !==
                                                                                            columnIndex
                                                                                              ? c
                                                                                              : {
                                                                                                  ...c,
                                                                                                  input:
                                                                                                    {
                                                                                                      ...c.input,
                                                                                                      uploadedJson:
                                                                                                        arr,
                                                                                                      jsonLabel:
                                                                                                        labelKey,
                                                                                                      jsonValue:
                                                                                                        valueKey,
                                                                                                      options,
                                                                                                    },
                                                                                                },
                                                                                        ),
                                                                                    };
                                                                                  },
                                                                                ),
                                                                            );
                                                                          } catch (err) {
                                                                            console.error(
                                                                              "Invalid JSON file",
                                                                              err,
                                                                            );
                                                                          }
                                                                        };
                                                                      reader.readAsText(
                                                                        file,
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    JSON Label
                                                                    Key
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.jsonLabel ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const val =
                                                                        e.target
                                                                          .value;
                                                                      setGrid(
                                                                        (
                                                                          prev,
                                                                        ) =>
                                                                          prev.map(
                                                                            (
                                                                              r,
                                                                              ri,
                                                                            ) => {
                                                                              if (
                                                                                ri !==
                                                                                rowIndex
                                                                              )
                                                                                return r;
                                                                              return {
                                                                                columns:
                                                                                  r.columns.map(
                                                                                    (
                                                                                      c,
                                                                                      ci,
                                                                                    ) => {
                                                                                      if (
                                                                                        ci !==
                                                                                        columnIndex
                                                                                      )
                                                                                        return c;
                                                                                      const uploaded =
                                                                                        c
                                                                                          .input
                                                                                          ?.uploadedJson ||
                                                                                        [];
                                                                                      const valueKey =
                                                                                        c
                                                                                          .input
                                                                                          ?.jsonValue ||
                                                                                        c
                                                                                          .input
                                                                                          ?.API
                                                                                          ?.value ||
                                                                                        "value";
                                                                                      return {
                                                                                        ...c,
                                                                                        input:
                                                                                          {
                                                                                            ...c.input,
                                                                                            jsonLabel:
                                                                                              val,
                                                                                            options:
                                                                                              uploaded.map(
                                                                                                (
                                                                                                  item,
                                                                                                ) => ({
                                                                                                  label:
                                                                                                    (item?.[
                                                                                                      val
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                  value:
                                                                                                    (item?.[
                                                                                                      valueKey
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                }),
                                                                                              ),
                                                                                          },
                                                                                      };
                                                                                    },
                                                                                  ),
                                                                              };
                                                                            },
                                                                          ),
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    JSON Value
                                                                    Key
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.jsonValue ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const val =
                                                                        e.target
                                                                          .value;
                                                                      setGrid(
                                                                        (
                                                                          prev,
                                                                        ) =>
                                                                          prev.map(
                                                                            (
                                                                              r,
                                                                              ri,
                                                                            ) => {
                                                                              if (
                                                                                ri !==
                                                                                rowIndex
                                                                              )
                                                                                return r;
                                                                              return {
                                                                                columns:
                                                                                  r.columns.map(
                                                                                    (
                                                                                      c,
                                                                                      ci,
                                                                                    ) => {
                                                                                      if (
                                                                                        ci !==
                                                                                        columnIndex
                                                                                      )
                                                                                        return c;
                                                                                      const uploaded =
                                                                                        c
                                                                                          .input
                                                                                          ?.uploadedJson ||
                                                                                        [];
                                                                                      const labelKey =
                                                                                        c
                                                                                          .input
                                                                                          ?.jsonLabel ||
                                                                                        c
                                                                                          .input
                                                                                          ?.API
                                                                                          ?.label ||
                                                                                        "label";
                                                                                      return {
                                                                                        ...c,
                                                                                        input:
                                                                                          {
                                                                                            ...c.input,
                                                                                            jsonValue:
                                                                                              val,
                                                                                            options:
                                                                                              uploaded.map(
                                                                                                (
                                                                                                  item,
                                                                                                ) => ({
                                                                                                  label:
                                                                                                    (item?.[
                                                                                                      labelKey
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                  value:
                                                                                                    (item?.[
                                                                                                      val
                                                                                                    ] ??
                                                                                                      String(
                                                                                                        item,
                                                                                                      )) +
                                                                                                    "",
                                                                                                }),
                                                                                              ),
                                                                                          },
                                                                                      };
                                                                                    },
                                                                                  ),
                                                                              };
                                                                            },
                                                                          ),
                                                                      );
                                                                    }}
                                                                  />
                                                                </div>

                                                                <div className="mt-2">
                                                                  {/* <Label>Preview Options</Label> */}
                                                                  {(() => {
                                                                    const inp =
                                                                      column?.input ||
                                                                      {};
                                                                    const isJsonSource =
                                                                      inp.isJson ||
                                                                      inp.sourceType ===
                                                                        "json";
                                                                    const previewOptions =
                                                                      isJsonSource
                                                                        ? (
                                                                            inp.uploadedJson ||
                                                                            []
                                                                          ).map(
                                                                            (
                                                                              item,
                                                                            ) => {
                                                                              const labelKey =
                                                                                inp.jsonLabel ||
                                                                                inp
                                                                                  .API
                                                                                  ?.label ||
                                                                                "label";
                                                                              const valueKey =
                                                                                inp.jsonValue ||
                                                                                inp
                                                                                  .API
                                                                                  ?.value ||
                                                                                "value";
                                                                              return {
                                                                                label:
                                                                                  (item?.[
                                                                                    labelKey
                                                                                  ] ??
                                                                                    String(
                                                                                      item,
                                                                                    )) +
                                                                                  "",
                                                                                value:
                                                                                  (item?.[
                                                                                    valueKey
                                                                                  ] ??
                                                                                    String(
                                                                                      item,
                                                                                    )) +
                                                                                  "",
                                                                              };
                                                                            },
                                                                          )
                                                                        : inp.options ||
                                                                          [];

                                                                    return (
                                                                      <></>
                                                                    );

                                                                    //   (
                                                                    //   <Input className="w-full" type="select" value="" onChange={() => {}}>
                                                                    //     <option value="">-- Available Options --</option>
                                                                    //     {previewOptions.map((option, idx) => (
                                                                    //       <option key={idx} value={option?.value}>
                                                                    //         {option?.label}
                                                                    //       </option>
                                                                    //     ))}
                                                                    //   </Input>
                                                                    // );
                                                                  })()}
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/****Enter API */}
                                                            {column?.input &&
                                                            column?.input
                                                              ?.type ===
                                                              "select" &&
                                                            (column?.input
                                                              ?.sourceType ===
                                                              "api" ||
                                                              !!column?.input
                                                                ?.isAPI) ? (
                                                              <div className="mt-2">
                                                                <div>
                                                                  <Label>
                                                                    Enter cURL
                                                                  </Label>
                                                                  <Input
                                                                    type="textarea"
                                                                    className="w-full"
                                                                    name={
                                                                      "cURL"
                                                                    }
                                                                    style={{
                                                                      minHeight:
                                                                        "80px",
                                                                    }}
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.cURL ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) => {
                                                                      handleChangeAPI(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "cURL",
                                                                      );
                                                                    }}
                                                                  />
                                                                  <small className="text-muted">
                                                                    Paste a
                                                                    complete
                                                                    cURL command
                                                                    (supports
                                                                    -X, -H, -d)
                                                                  </small>
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Response
                                                                    Path
                                                                    (optional)
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    className="w-full"
                                                                    name={
                                                                      "response_data_mapping"
                                                                    }
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.response_data_mapping ||
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) => {
                                                                      handleChangeAPI(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "response_data_mapping",
                                                                      );
                                                                    }}
                                                                    placeholder="e.g. customers or data.customers"
                                                                  />
                                                                </div>

                                                                <div className="mt-2">
                                                                  <Label>
                                                                    Select Label
                                                                    (Field Name)
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    className="w-full"
                                                                    name={
                                                                      "APILabel"
                                                                    }
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.label
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) => {
                                                                      handleChangeAPI(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "label",
                                                                      );
                                                                    }}
                                                                    placeholder="label"
                                                                  />
                                                                </div>
                                                                <div className="mt-1">
                                                                  <Label>
                                                                    Select value
                                                                    (Field Name)
                                                                  </Label>
                                                                  <Input
                                                                    type="text"
                                                                    className="w-full"
                                                                    name={
                                                                      "APIValue"
                                                                    }
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.API
                                                                        ?.value
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) => {
                                                                      handleChangeAPI(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "value",
                                                                      );
                                                                    }}
                                                                    placeholder="value"
                                                                  />
                                                                </div>

                                                                <div className="mt-2 d-flex justify-content-between">
                                                                  <Label>
                                                                    Fetch Option
                                                                    Details{" "}
                                                                  </Label>
                                                                  <Button
                                                                    color="primary"
                                                                    size="sm"
                                                                    style={{
                                                                      marginLeft:
                                                                        "auto",
                                                                      display:
                                                                        "block",
                                                                      height:
                                                                        "20px",
                                                                      fontSize:
                                                                        "10px",
                                                                      paddingTop:
                                                                        "1px",
                                                                    }}
                                                                    outline
                                                                    onClick={() => {
                                                                      fetchAPI(
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "apiOptions",
                                                                      );
                                                                    }}
                                                                  >
                                                                    API Call
                                                                  </Button>
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* Conditional Rendering */}
                                                            {column?.input
                                                              ?.isCondition ? (
                                                              <div>
                                                                <div className="mt-1">
                                                                  <Label>
                                                                    Conditional
                                                                    Render If
                                                                  </Label>
                                                                  <Input
                                                                    className="w-full"
                                                                    type="select"
                                                                    value={(() => {
                                                                      const key =
                                                                        column
                                                                          ?.input
                                                                          ?.conditionalRender
                                                                          ?.key;
                                                                      const idx =
                                                                        conditionOptions.findIndex(
                                                                          (o) =>
                                                                            o.key ===
                                                                            key,
                                                                        );
                                                                      return idx >
                                                                        -1
                                                                        ? String(
                                                                            idx,
                                                                          )
                                                                        : "";
                                                                    })()}
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      addConditions(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "condition-key",
                                                                      )
                                                                    }
                                                                  >
                                                                    <option value="">
                                                                      -- Select
                                                                      field key
                                                                      --
                                                                    </option>
                                                                    {conditionOptions.filter(
                                                                      (option) => !(option.rIdx === rowIndex && option.cIdx === columnIndex)
                                                                    ).map(
                                                                      (
                                                                        option,
                                                                        index,
                                                                      ) => (
                                                                        <option
                                                                          key={
                                                                            index
                                                                          }
                                                                          value={String(
                                                                            conditionOptions.findIndex((o) => o.key === option.key),
                                                                          )}
                                                                        >
                                                                          {option.label || option.key} ({option.type})
                                                                        </option>
                                                                      ),
                                                                    )}
                                                                  </Input>
                                                                </div>

                                                                <div className="mt-1">
                                                                  <Label>
                                                                    Equal to
                                                                  </Label>
                                                                  {(column?.input?.tempOptions ?? []).length > 0 ? (
                                                                    <Input
                                                                      className="w-full"
                                                                      type="select"
                                                                      value={column?.input?.conditionalRender?.value ?? ""}
                                                                      onChange={(event) =>
                                                                        addConditions(event, rowIndex, columnIndex, "condition-value")
                                                                      }
                                                                    >
                                                                      <option value="">-- Select value --</option>
                                                                      {(column?.input?.tempOptions ?? []).map((option, index) => (
                                                                        <option key={index} value={option.value}>
                                                                          {option.key}
                                                                        </option>
                                                                      ))}
                                                                    </Input>
                                                                  ) : (
                                                                    <Input
                                                                      className="w-full"
                                                                      type={
                                                                        column?.input?.conditionalRender?.controllingType === 'number'
                                                                          ? 'number'
                                                                          : column?.input?.conditionalRender?.controllingType === 'date'
                                                                          ? 'date'
                                                                          : column?.input?.conditionalRender?.controllingType === 'time'
                                                                          ? 'time'
                                                                          : 'text'
                                                                      }
                                                                      placeholder="Enter value to match"
                                                                      value={column?.input?.conditionalRender?.value ?? ""}
                                                                      onChange={(event) =>
                                                                        addConditions(event, rowIndex, columnIndex, "condition-value")
                                                                      }
                                                                    />
                                                                  )}
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* Unified Options Preview - single select for all source types */}
                                                            {column?.input &&
                                                            column?.input
                                                              ?.type ===
                                                              "select" ? (
                                                              <div className="mt-2">
                                                                <Label>
                                                                  Options
                                                                  Preview
                                                                </Label>
                                                                {/* Scrollable preview that appends on scroll for schema/api sources */}
                                                                {(() => {
                                                                  const key =
                                                                    "schemaOptions" +
                                                                    rowIndex +
                                                                    columnIndex;
                                                                  const srcOptions =
                                                                    column
                                                                      ?.input
                                                                      ?.options &&
                                                                    column.input
                                                                      .options
                                                                      .length
                                                                      ? column
                                                                          .input
                                                                          .options
                                                                      : apiOptions?.[
                                                                          key
                                                                        ]
                                                                          ?.items ||
                                                                        apiOptions?.[
                                                                          "apiOptions" +
                                                                            rowIndex +
                                                                            columnIndex
                                                                        ] ||
                                                                        [];

                                                                  const loadMore =
                                                                    () => {
                                                                      const curState =
                                                                        apiOptions?.[
                                                                          key
                                                                        ] || {
                                                                          items:
                                                                            [],
                                                                          page: 0,
                                                                          hasMore: true,
                                                                        };
                                                                      const nextPage =
                                                                        (curState.page ||
                                                                          0) +
                                                                        1;

                                                                      setApiOptions(
                                                                        (
                                                                          prev,
                                                                        ) => {
                                                                          const cur =
                                                                            prev?.[
                                                                              key
                                                                            ] || {
                                                                              items:
                                                                                [],
                                                                              page: 0,
                                                                              hasMore: true,
                                                                            };
                                                                          if (
                                                                            !cur.hasMore
                                                                          )
                                                                            return prev;
                                                                          return {
                                                                            ...prev,
                                                                            [key]:
                                                                              {
                                                                                ...cur,
                                                                                _loading: true,
                                                                              },
                                                                          };
                                                                        },
                                                                      );

                                                                      // call fetch with the computed nextPage
                                                                      fetchTableData(
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        nextPage,
                                                                        500,
                                                                      ).finally(
                                                                        () => {
                                                                          // clear _loading flag (fetchTableData will overwrite key with proper object)
                                                                          setApiOptions(
                                                                            (
                                                                              prev,
                                                                            ) => {
                                                                              const p =
                                                                                {
                                                                                  ...(prev?.[
                                                                                    key
                                                                                  ] ||
                                                                                    {}),
                                                                                };
                                                                              delete p._loading;
                                                                              return {
                                                                                ...prev,
                                                                                [key]:
                                                                                  p,
                                                                              };
                                                                            },
                                                                          );
                                                                        },
                                                                      );
                                                                    };

                                                                  return (
                                                                    <div
                                                                      className="w-full"
                                                                      style={{
                                                                        border:
                                                                          "1px solid #ced4da",
                                                                        borderRadius: 4,
                                                                        maxHeight: 300,
                                                                        overflowY:
                                                                          "auto",
                                                                        padding: 6,
                                                                      }}
                                                                      onScroll={(
                                                                        e,
                                                                      ) => {
                                                                        const el =
                                                                          e.target;
                                                                        if (
                                                                          el.scrollTop +
                                                                            el.clientHeight >=
                                                                          el.scrollHeight -
                                                                            120
                                                                        ) {
                                                                          const keyLocal =
                                                                            key;
                                                                          const state =
                                                                            apiOptions?.[
                                                                              keyLocal
                                                                            ] || {
                                                                              items:
                                                                                [],
                                                                              page: 0,
                                                                              hasMore: true,
                                                                              _loading: false,
                                                                            };
                                                                          if (
                                                                            !state._loading &&
                                                                            state.hasMore
                                                                          )
                                                                            loadMore();
                                                                        }
                                                                      }}
                                                                    >
                                                                      {srcOptions.length ===
                                                                      0 ? (
                                                                        <div
                                                                          style={{
                                                                            padding: 8,
                                                                            color:
                                                                              "#6c757d",
                                                                          }}
                                                                        >
                                                                          No
                                                                          options
                                                                        </div>
                                                                      ) : (
                                                                        srcOptions.map(
                                                                          (
                                                                            option,
                                                                            idx,
                                                                          ) => (
                                                                            <div
                                                                              key={
                                                                                idx
                                                                              }
                                                                              style={{
                                                                                padding:
                                                                                  "6px 8px",
                                                                                borderBottom:
                                                                                  "1px solid #f1f1f1",
                                                                              }}
                                                                            >
                                                                              {option?.label ??
                                                                                option?.[
                                                                                  column
                                                                                    ?.input
                                                                                    ?.optionKey
                                                                                ] ??
                                                                                option?.name ??
                                                                                option?.[
                                                                                  column
                                                                                    ?.input
                                                                                    ?.API
                                                                                    ?.label
                                                                                ]}
                                                                            </div>
                                                                          ),
                                                                        )
                                                                      )}
                                                                      {apiOptions?.[
                                                                        key
                                                                      ]
                                                                        ?.hasMore ? (
                                                                        <div
                                                                          style={{
                                                                            padding: 8,
                                                                            textAlign:
                                                                              "center",
                                                                            color:
                                                                              "#6c757d",
                                                                          }}
                                                                        >
                                                                          Loading
                                                                          more...
                                                                        </div>
                                                                      ) : null}
                                                                    </div>
                                                                  );
                                                                })()}
                                                              </div>
                                                            ) : null}

                                                            {/* Filter by Previous Field */}
                                                            {column?.input
                                                              ?.type ===
                                                            "select" ? (
                                                              <div className="mt-2 border-top pt-2">
                                                                <div className="d-flex align-items-center gap-2">
                                                                  <Input
                                                                    className="checkbox_animated"
                                                                    type="checkbox"
                                                                    checked={
                                                                      !!column
                                                                        ?.input
                                                                        ?.ifFilterEnable
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) =>
                                                                      handleChange(
                                                                        e,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                        "ifFilterEnable",
                                                                      )
                                                                    }
                                                                  />
                                                                  <Label className="mb-0 fw-bold">
                                                                    Filter by
                                                                    Previous
                                                                    Field
                                                                  </Label>
                                                                </div>

                                                                {!!column?.input
                                                                  ?.ifFilterEnable && (
                                                                  <div className="mt-2">
                                                                    <div className="mt-1">
                                                                      <Label>
                                                                        Previous
                                                                        Form
                                                                        Field
                                                                        Key
                                                                      </Label>
                                                                      <Input
                                                                        type="select"
                                                                        value={
                                                                          column
                                                                            ?.input
                                                                            ?.previousFormKey ||
                                                                          ""
                                                                        }
                                                                        onChange={(
                                                                          e,
                                                                        ) =>
                                                                          handleChange(
                                                                            e,
                                                                            rowIndex,
                                                                            columnIndex,
                                                                            "previousFormKey",
                                                                          )
                                                                        }
                                                                      >
                                                                        <option value="">
                                                                          --
                                                                          Select
                                                                          parent
                                                                          field
                                                                          --
                                                                        </option>
                                                                        {grid.flatMap(
                                                                          (
                                                                            r,
                                                                            ri,
                                                                          ) =>
                                                                            r.columns
                                                                              .map(
                                                                                (
                                                                                  c,
                                                                                  ci,
                                                                                ) => {
                                                                                  if (
                                                                                    ri ===
                                                                                      rowIndex &&
                                                                                    ci ===
                                                                                      columnIndex
                                                                                  )
                                                                                    return null;
                                                                                  const inp =
                                                                                    c?.input;
                                                                                  if (
                                                                                    !inp ||
                                                                                    !inp.key
                                                                                  )
                                                                                    return null;
                                                                                  return (
                                                                                    <option
                                                                                      key={`${ri}-${ci}`}
                                                                                      value={
                                                                                        inp.key
                                                                                      }
                                                                                    >
                                                                                      {inp.label ||
                                                                                        inp.key}
                                                                                    </option>
                                                                                  );
                                                                                },
                                                                              )
                                                                              .filter(
                                                                                Boolean,
                                                                              ),
                                                                        )}
                                                                      </Input>
                                                                    </div>
                                                                    <div className="mt-1">
                                                                      <Label>
                                                                        Filter
                                                                        Key
                                                                      </Label>
                                                                      <Input
                                                                        type="text"
                                                                        placeholder="e.g. country_id"
                                                                        value={
                                                                          column
                                                                            ?.input
                                                                            ?.filterKey ||
                                                                          ""
                                                                        }
                                                                        onChange={(
                                                                          e,
                                                                        ) =>
                                                                          handleChange(
                                                                            e,
                                                                            rowIndex,
                                                                            columnIndex,
                                                                            "filterKey",
                                                                          )
                                                                        }
                                                                      />
                                                                      <small className="text-muted">
                                                                        Key in
                                                                        this
                                                                        field&apos;s
                                                                        options
                                                                        matched
                                                                        against
                                                                        the
                                                                        parent
                                                                        field&apos;s
                                                                        selected
                                                                        value.
                                                                      </small>
                                                                    </div>
                                                                  </div>
                                                                )}
                                                              </div>
                                                            ) : null}

                                                            {/* Radio Options */}
                                                            {column?.input
                                                              ?.type ===
                                                            "radio" ? (
                                                              <div className="mt-2 border-top pt-2">
                                                                <Label className="fw-bold">
                                                                  Radio Options
                                                                </Label>
                                                                <div className="mt-1">
                                                                  <Label>
                                                                    Label
                                                                  </Label>
                                                                  <Input
                                                                    className="w-full"
                                                                    type="text"
                                                                    name="radio_label"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.radioOptions?.[
                                                                        (column
                                                                          ?.input
                                                                          ?.radioOptions
                                                                          ?.length ??
                                                                          1) - 1
                                                                      ]
                                                                        ?.label ??
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      handleRadioOptions(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                      )
                                                                    }
                                                                  />
                                                                  <Label className="mt-2">
                                                                    Key
                                                                  </Label>
                                                                  <Input
                                                                    className="w-full"
                                                                    type="text"
                                                                    name="radio_key"
                                                                    readOnly
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.radioOptions?.[
                                                                        (column
                                                                          ?.input
                                                                          ?.radioOptions
                                                                          ?.length ??
                                                                          1) - 1
                                                                      ]?.key ??
                                                                      ""
                                                                    }
                                                                  />
                                                                </div>
                                                                <Button
                                                                  className="mt-2"
                                                                  onClick={() =>
                                                                    addRadioOptions(
                                                                      rowIndex,
                                                                      columnIndex,
                                                                    )
                                                                  }
                                                                >
                                                                  Add
                                                                </Button>
                                                                <div className="mt-2">
                                                                  {(
                                                                    column
                                                                      ?.input
                                                                      ?.radioOptions ??
                                                                    []
                                                                  ).map(
                                                                    (
                                                                      opt,
                                                                      index,
                                                                    ) => (
                                                                      <div
                                                                        key={`ropt-${index}`}
                                                                        className="d-flex mt-1 align-items-center gap-2"
                                                                      >
                                                                        <Input
                                                                          type="radio"
                                                                          name={`preview_radio_${rowIndex}_${columnIndex}`}
                                                                          disabled
                                                                          readOnly
                                                                        />
                                                                        <p className="fw-bold mb-0">
                                                                          {opt.label ||
                                                                            opt.key}
                                                                        </p>
                                                                        {(column
                                                                          ?.input
                                                                          ?.radioOptions
                                                                          ?.length ??
                                                                          0) >
                                                                          1 && (
                                                                          <FeatherIcon
                                                                            icon="x"
                                                                            size={
                                                                              14
                                                                            }
                                                                            className="text-danger cursor-pointer"
                                                                            onClick={() =>
                                                                              removeRadioOption(
                                                                                rowIndex,
                                                                                columnIndex,
                                                                                index,
                                                                              )
                                                                            }
                                                                          />
                                                                        )}
                                                                      </div>
                                                                    ),
                                                                  )}
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* Checkbox Options */}
                                                            {column?.input
                                                              ?.checkboxOptions ? (
                                                              <div>
                                                                <div className="mt-1">
                                                                  <Label>
                                                                    Label
                                                                  </Label>
                                                                  <Input
                                                                    className="w-full"
                                                                    type="text"
                                                                    name="checkbox_label"
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.checkboxOptions[
                                                                        column
                                                                          ?.input
                                                                          ?.checkboxOptions
                                                                          .length -
                                                                          1
                                                                      ]
                                                                        ?.label ??
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      event,
                                                                    ) =>
                                                                      handleCheckboxOptions(
                                                                        event,
                                                                        rowIndex,
                                                                        columnIndex,
                                                                      )
                                                                    }
                                                                  />
                                                                  <Label className="mt-2">
                                                                    Key
                                                                  </Label>
                                                                  <Input
                                                                    className="w-full"
                                                                    type="text"
                                                                    name="checkbox_key"
                                                                    readOnly
                                                                    value={
                                                                      column
                                                                        ?.input
                                                                        ?.checkboxOptions[
                                                                        column
                                                                          ?.input
                                                                          ?.checkboxOptions
                                                                          .length -
                                                                          1
                                                                      ]?.key ??
                                                                      ""
                                                                    }
                                                                  />
                                                                </div>
                                                                <Button
                                                                  className="mt-2"
                                                                  onClick={() =>
                                                                    addCheckboxOptions(
                                                                      rowIndex,
                                                                      columnIndex,
                                                                    )
                                                                  }
                                                                >
                                                                  Add
                                                                </Button>
                                                                <div>
                                                                  {(
                                                                    column
                                                                      ?.input
                                                                      ?.checkboxOptions ??
                                                                    []
                                                                  ).map(
                                                                    (
                                                                      checkbox,
                                                                      index,
                                                                    ) => (
                                                                      <div
                                                                        key={`cbox-${index}`}
                                                                        className="d-flex mt-2 mr-2 align-middle"
                                                                      >
                                                                        <Input
                                                                          className="checkbox_animated"
                                                                          type="checkbox"
                                                                          name={`${checkbox.key}`}
                                                                          id={`checkboxline${index}`}
                                                                        />
                                                                        <p className="fw-bold mb-0 ms-2">
                                                                          {
                                                                            checkbox.key
                                                                          }
                                                                        </p>
                                                                      </div>
                                                                    ),
                                                                  )}
                                                                </div>
                                                              </div>
                                                            ) : null}

                                                            {/* Note HTML Editor */}
                                                            {(column?.input?.type === "note" || column?.input?.isPreview) && (
                                                              <div className="mt-3 border-top pt-2">
                                                                <Label className="fw-bold">
                                                                  Note (HTML)
                                                                </Label>
                                                                <ReactQuill
                                                                  theme="snow"
                                                                  value={
                                                                    column
                                                                      ?.input
                                                                      ?.noteHtml ||
                                                                    ""
                                                                  }
                                                                  onChange={(
                                                                    val,
                                                                  ) =>
                                                                    handleNoteHtmlChange(
                                                                      val,
                                                                      rowIndex,
                                                                      columnIndex,
                                                                    )
                                                                  }
                                                                  style={{
                                                                    background:
                                                                      "#fff",
                                                                  }}
                                                                />
                                                              </div>
                                                            )}
                                                          </CardBody>
                                                        )}
                                                      </Card>
                                                    ) : (
                                                      <div className="d-flex w-full custom-drag-here">
                                                        <div
                                                          {...colDrag.dragHandleProps}
                                                          style={{
                                                            cursor: "grab",
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            padding: "0 4px",
                                                          }}
                                                          title="Drag to reorder"
                                                        >
                                                          <FeatherIcon
                                                            icon="move"
                                                            size={14}
                                                            className="text-muted"
                                                          />
                                                        </div>
                                                        <div className="d-flex w-full justify-content-center align-middle">
                                                          <span>drag here</span>
                                                        </div>
                                                        <div className="d-flex custom-x-width justify-content-center align-middle em-2">
                                                          <FeatherIcon
                                                            icon="x"
                                                            fill="black"
                                                            className="w-10 text-black cursor-pointer"
                                                            onClick={() =>
                                                              removeColumn(
                                                                rowIndex,
                                                                columnIndex,
                                                              )
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                              </Draggable>
                                            );
                                          },
                                        )}
                                        {colsProvided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                  <div className="d-flex custom-column ms-2">
                                    <div>
                                      <FeatherIcon
                                        icon="columns"
                                        onClick={() => addColumn(rowIndex)}
                                        className="cursor-pointer text-black"
                                      />
                                    </div>{" "}
                                    <div>
                                      <FeatherIcon
                                        icon="trash-2"
                                        className="text-black cursor-pointer text-danger ms-1"
                                        onClick={() => removeRow(rowIndex)}
                                      />
                                    </div>
                                    <div className="ms-1">
                                      <button
                                        type="button"
                                        title={
                                          row.enableAddMore
                                            ? "Disable Add More"
                                            : "Enable Add More"
                                        }
                                        onClick={() =>
                                          toggleRowAddMore(rowIndex)
                                        }
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 600,
                                          padding: "3px 8px",
                                          borderRadius: "12px",
                                          border: row.enableAddMore
                                            ? "1px solid #28a745"
                                            : "1px solid #adb5bd",
                                          background: row.enableAddMore
                                            ? "#d4edda"
                                            : "#f8f9fa",
                                          color: row.enableAddMore
                                            ? "#155724"
                                            : "#6c757d",
                                          cursor: "pointer",
                                          whiteSpace: "nowrap",
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {row.enableAddMore
                                          ? "+ Add More"
                                          : "+ Add More"}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {rowsProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>

                <div className="d-flex justify-content-center mt-2">
                  <FeatherIcon
                    icon="plus-square"
                    onClick={addGrid}
                    className="cursor-pointer text-black"
                  />
                </div>
              </DragDropContext>
            </div>

            {/* Palette */}
            <div className="col-2">
              {PALETTE.map((input, inputIndex) => (
                <div
                  key={`palette-${inputIndex}`}
                  className="custom-input-fields-options"
                  onMouseDown={(event) => startDrag(event, inputIndex)}
                  style={{
                    width: "150px",
                    height: "50px",
                    backgroundColor: "lightblue",
                    border: "1px solid #000",
                    position: "relative",
                    cursor: "grab",
                    userSelect: "none",
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Drag and drop onto the grid"
                >
                  <p className="mb-0">{input.name}</p>
                </div>
              ))}
            </div>

            {/* Drag Ghost */}
            {dragState.active && (
              <div
                style={{
                  position: "fixed",
                  top: dragState.pos.y + 8,
                  left: dragState.pos.x + 8,
                  width: 150,
                  height: 50,
                  background: "rgba(0,0,0,0.1)",
                  border: "1px dashed #333",
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  zIndex: 9999,
                }}
              >
                {dragState.item?.name}
              </div>
            )}
          </div>
        ) : (
          // JSON mode
          <div>
            <Input
              type="textarea"
              onChange={handleJSONInput}
              value={jsonDataStr}
              style={{ minHeight: 220 }}
            />
            {jsonValidator?.error && (
              <span className="mt-1 text-danger">{jsonValidator?.error}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default FormBuilder;
